﻿import ko from "knockout";
import $ from 'jquery';
import Utility from "./utility";
import { IBinder, IBehaviorBinding, ICellBinding, IExternalElementBinder, BindingConverter } from "./binders/ibinder"
import { TemplateRowFactory, ITemplateRow } from "./templaterowfactory"
import { DateInputBinder } from "./binders/dateinputbinder";
import { SelectBinder } from "./binders/selectbinder";
import { NumericInputBinder } from "./binders/numericinputbinder";
import { MoneyInputBinder } from "./binders/moneyinputbinder";
import { TextInputBinder } from "./binders/textinputbinder";
import { HiddenInputBinder } from "./binders/hiddeninputbinder";
import { CheckboxInputBinder } from "./binders/checkboxinputbinder";
import { ColumnTotalBinder } from "./binders/columntotalbinder";
import { RowCountBinder } from "./binders/rowcountbinder";
import { ExpressionBinder } from "./binders/expressionbinder";
import { EnterKeyBinder } from "./binders/enterkeybinder"
import { RemoveRowBinder } from "./binders/removerowbinder"
import { RemoteOnChangeBinder } from "./binders/remoteonchangebinder"
import { AutoGrowBinder } from "./binders/autogrowbinder"
import { AddRowBinder } from "./binders/addrowbinder"
import { CheckAllBinder } from "./binders/checkallbinder"
import { ForEachBinder } from "./binders/foreachbinder"
import { ToJSONBinder } from "./binders/tojsonbinder"
import { ExternalJsonBinder } from "./binders/externaljsonbinder"
import { ExternalTrackChangesHideBinder } from "./binders/externaltrackchangeshidebinder"
import { ExternalNavigationWarning } from "./binders/externalnavigationwarningbinder"

declare global {
  interface JQuery {
    editortable(): EditorTable;
  }
}

export class EditorTable {

  static get wireup(): string {
    return '.editortable';
  }
  static isEditorTable($element: JQuery): boolean {
    return $element.is(this.wireup);
  }

  tableKey: string;
  tableData: any = ko.observable({
    Rows: ko.observableArray()
  });
  computedTableData: any;
  private $table: JQuery;
  private valueBinders = new Array<IBinder>();
  private behaviorBinders = new Array<IBinder>();
  private externalBinders = new Array<IExternalElementBinder>();
  private templateRow: ITemplateRow;
  private emptyObjectBuilder: Object;

  constructor(private $root: JQuery) {
    if ($root.is("table"))
      this.$table = $root;
    else
      this.$table = $root.find("table").first();

    if (this.$table.closest(".editortable").length == 0)
      this.$table.addClass('editortable');

    this.tableKey = this.$table.attr('data-et-key');
    if (!this.tableKey)
      throw new Error("your editor table must have a data-et-key attribute to identify it");

    this.registerBinders();
    this.initialize();
  }

  private initialize(): void {
    var data = { tableKey: this.tableKey };
    this.templateRow = TemplateRowFactory.create(this.$table);
    this.bindRows(data);
    this.bindRootValues(data);
    this.bindRootBehavior(data);
    this.bindExternal(data);
    this.templateRow.init();
    data["emptyObjectBuilder"] = this.emptyObjectBuilder;
    this.tableData = ko.observable(data);
    ko.applyBindings(this.tableData, this.$root[0]);
    this.$table.data("rustig.editortable", this);
  }

  private bindRootBehavior(data): void {
    var binders = this.behaviorBinders;
    for (var i = 0; i < binders.length; i++) {
      var binder = binders[i];
      var $elements = binder.getElements(this.$root);

      $elements.each(function () {
        var $element = $(this);
        var binding = BindingConverter.toBehaviorBinding(binder.getBinding($element, data));
        if (binding && !EditorTable.inTable($element)) {

          EditorTable.addBindingToElement($element, binding);
          if (binding.behavior)
            data[binding.behavior.name] = binding.behavior.build(data);
        }
      });
    }
  }

  private bindRootValues(data): void {
    var binders = this.valueBinders;
    for (var i = 0; i < binders.length; i++) {
      var binder = binders[i];
      var $elements = binder.getElements(this.$root);

      $elements.each(function () {
        var $element = $(this);
        if (!EditorTable.inTable($element) && !EditorTable.isInitialized($element)) {
          var binding = BindingConverter.toValueBinding(binder.getBinding($element, data));
          if (binding) {
            data[Utility.relativeName(binding.dataName)] = binding.dataValue;
            EditorTable.addBindingToElement($element, binding);
            $element.data("et-initialized", "true");
          }
        }
      });
    }
  }

  private bindRows(data) {
    data["Rows"] = ko.observableArray();
    var valueBinders = this.valueBinders;
    var behaviorBinders = this.behaviorBinders;
    var $templateRow = this.templateRow.$row;
    var emptyObjectBuilder = [];
    this.$table.find("tbody tr").each(function (i, v) {
      var $row = $(this);
      var rowNumber = i;
      var rowData = {};
      $row.attr("data-init", "true");

      valueBinders.forEach(binder => {
        var $cells = binder.getElements($row);

        $cells.each(function () {
          var $cell = $(this);
          var binding = BindingConverter.toCellBinding(binder.getBinding($cell, rowData));
          if (!binding || $cell.data("et-initialized"))
            return;

          EditorTable.addBindingToElement($cell, binding, rowNumber);

          rowData[Utility.relativeName(binding.dataName)] = binding.dataValue;
          if ($row.is($templateRow))
            emptyObjectBuilder.push({ name: Utility.relativeName(binding.dataName), build: binding.createEmptyValue });

          $cell.data("et-initialized", "true");

        });
      });

      behaviorBinders.forEach(binder => {
        var $cells = binder.getElements($row);

        $cells.each(function () {
          var $cell = $(this);
          var binding = BindingConverter.toBehaviorBinding(binder.getBinding($cell, rowData));
          if (!binding)
            return;

          EditorTable.addBindingToElement($cell, binding);

          if (binding.behavior)
            data[binding.behavior.name] = binding.behavior.build(data);

        });
      });

      rowData["editorTableInternalId"] = Utility.guid();
      if (!$row.is($templateRow))
        data.Rows.push(rowData);
    });
    this.emptyObjectBuilder = emptyObjectBuilder;
  }

  private bindExternal(data): void {
    var self = this;
    self.externalBinders.forEach(b => {
      var binder = b;
      self[binder.key] = binder.compute(data);
      self[binder.key].subscribe((value) => {
        binder.subscribe(value);
      });
      var dataExtension = binder.extend();
      if (dataExtension)
        data[dataExtension.name] = dataExtension.build(self.tableData);
      self[binder.key].notifySubscribers(self[binder.key]());
    });
  }


  private registerBinders(): void {
    //most specific binders first
    this.valueBinders.push(new DateInputBinder());
    this.valueBinders.push(new SelectBinder());
    this.valueBinders.push(new NumericInputBinder());
    this.valueBinders.push(new MoneyInputBinder());
    this.valueBinders.push(new TextInputBinder());
    this.valueBinders.push(new HiddenInputBinder());
    this.valueBinders.push(new CheckboxInputBinder());
    this.valueBinders.push(new ColumnTotalBinder());
    this.valueBinders.push(new RowCountBinder());
    //computed cell binders should be processed last so all other values are initialized
    this.valueBinders.push(new ExpressionBinder());

    this.behaviorBinders.push(new EnterKeyBinder());
    this.behaviorBinders.push(new RemoveRowBinder());
    this.behaviorBinders.push(new RemoteOnChangeBinder());
    this.behaviorBinders.push(new AutoGrowBinder());
    this.behaviorBinders.push(new AddRowBinder());
    this.behaviorBinders.push(new CheckAllBinder());
    this.behaviorBinders.push(new ForEachBinder());
    this.behaviorBinders.push(new ToJSONBinder());

    //external element binders. bind to elements outside the KO/root scope
    this.externalBinders.push(new ExternalJsonBinder(this.tableKey));
    this.externalBinders.push(new ExternalTrackChangesHideBinder(this.tableKey));
    this.externalBinders.push(new ExternalNavigationWarning(this.tableKey));
  }

  private static inTable($element: JQuery): boolean {
    return $element.parents("tbody").length !== 0;
  }

  private static isInitialized($element: JQuery): boolean {
    return $element.data("et-initialized");
  }

  private static addBindingToElement($element: JQuery, binding: any, rowNumber?: Number) {
    var currentBinding = $element.attr("data-bind");
    var finalBinding = "";
    //FIF: Remove this hack for having multiple 'event' binders
    if (currentBinding && binding.bindingName == "event" && currentBinding.indexOf("event") != -1) {
      var bindingValue = binding.bindTo.substr(1, binding.bindTo.length - 2);
      var eventClosingBracketPosition = currentBinding.indexOf("}");
      finalBinding = [currentBinding.slice(0, eventClosingBracketPosition), ", ", bindingValue, currentBinding.slice(eventClosingBracketPosition)].join('');
    }
    else {
      var newBinding = binding.bindingName + ": "
        + (binding.dataName ? Utility.relativeName(binding.dataName) : binding.bindTo);

      finalBinding = currentBinding ? currentBinding + ", " + newBinding : newBinding;
    }

    $element.attr("data-bind", finalBinding);
    if (rowNumber) {
      var elementId = $element.attr("id");
      if (elementId) {
        $element.attr("id", elementId + "_" + rowNumber);
      }
    }
  }
}

($ => {
  function Plugin() {
    return this.each(function () {
      var $this = $(this);
      var table = $this.data('rustig.editortable');

      if (!table) {
        new EditorTable($this);
      }
      else {
        throw new Error("refresh not implemented");
      }
    });
  }
  $.fn.editortable = Plugin;

})($);